/**
 * Applied Mastery Banner — SAA's institutional messaging.
 * "Don't just learn the play. Know when to run it."
 *
 * Renders in 4 distinct variants for the 4 placement targets defined by Coach B.
 */
import React from 'react';
import { Target } from 'lucide-react';

const GOLD = '#D4AF37';
const ORANGE = '#FF5722';
const GRID_BG = '#0B0E14';
const SUB_TEXT = '#E2E8F0';

/** Target A — Student Daily Cockpit Header (dark grid card, gold accent line) */
export const AppliedMasteryCockpit = () => (
  <div
    className="relative border max-w-7xl mx-auto overflow-hidden"
    style={{ backgroundColor: GRID_BG, borderColor: `${GOLD}55` }}
    data-testid="applied-mastery-cockpit"
  >
    <div className="absolute inset-x-0 top-0 h-[2px]" style={{ backgroundColor: GOLD }} />
    <div className="absolute inset-x-0 bottom-0 h-[2px]" style={{ backgroundColor: `${GOLD}66` }} />
    {/* faint grid texture */}
    <div
      className="absolute inset-0 opacity-[0.08] pointer-events-none"
      style={{
        backgroundImage: `linear-gradient(${GOLD} 1px, transparent 1px), linear-gradient(90deg, ${GOLD} 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
      }}
    />
    <div className="relative px-5 py-5 sm:px-6 sm:py-6 md:px-10 md:py-8">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
        <span className="text-[10px] tracking-[0.4em] font-black uppercase" style={{ color: GOLD }}>
          The SAA Standard
        </span>
      </div>
      <h2
        className="text-white font-black uppercase leading-[1.05] text-xl sm:text-2xl md:text-4xl tracking-tight break-words"
        style={{ color: '#FFFFFF' }}
      >
        Don't just learn the play.<br />
        <span style={{ color: GOLD }}>Know when to run it.</span>
      </h2>
      <p
        className="mt-3 text-sm md:text-[15px] leading-relaxed max-w-3xl break-words"
        style={{ color: SUB_TEXT }}
      >
        In sports and in life, timing is everything. We don't just teach the theory— we train our scholars to recognize <span className="font-bold text-white">when</span> and <span className="font-bold text-white">how</span> to apply the theory.
      </p>
    </div>
  </div>
);

/** Target B — Socratic AI Tutor Welcome Screen ("THE SAA STANDARD" gold badge) */
export const AppliedMasteryTutorWelcome = () => (
  <div
    className="relative border-2 p-6 md:p-8 mx-auto max-w-2xl"
    style={{ backgroundColor: GRID_BG, borderColor: GOLD }}
    data-testid="applied-mastery-tutor"
  >
    <div className="flex items-center justify-center mb-4">
      <div
        className="inline-flex items-center gap-2 border-2 px-4 py-1.5"
        style={{ borderColor: GOLD, backgroundColor: `${GOLD}11` }}
      >
        <Target className="w-3 h-3" style={{ color: GOLD }} />
        <span className="text-[10px] tracking-[0.4em] font-black uppercase" style={{ color: GOLD }}>
          The SAA Standard
        </span>
      </div>
    </div>
    <h3 className="text-center text-white font-black uppercase leading-tight text-xl md:text-2xl">
      Don't just learn the play.<br />
      <span style={{ color: GOLD }}>Know when to run it.</span>
    </h3>
    <p className="mt-3 text-center text-xs md:text-sm" style={{ color: SUB_TEXT }}>
      In sports and in life, timing is everything. Ask me anything — I'll teach you
      how to <span className="font-bold text-white">recognize the moment</span>.
    </p>
  </div>
);

/** Target C — Elective Course Hub & Module Cover Cards (executive quote block, orange highlights) */
export const AppliedMasteryElective = () => (
  <div
    className="relative border-l-4 py-4 px-5 md:px-6"
    style={{ backgroundColor: '#0A0A0E', borderLeftColor: ORANGE }}
    data-testid="applied-mastery-elective"
  >
    <div className="text-[9px] tracking-[0.4em] font-black uppercase mb-2" style={{ color: ORANGE }}>
      Applied Mastery · Coach B
    </div>
    <blockquote className="text-white text-base md:text-lg font-bold leading-snug italic">
      "Don't just learn the play. <span style={{ color: ORANGE }}>Know when to run it.</span> In sports and in life, timing is everything. We don't just teach the theory— we train our scholars to recognize when and how to <span style={{ color: ORANGE }}>apply the theory</span>."
    </blockquote>
  </div>
);

/** Target D — Parent Mastery Portal Header (execution-over-memorization variant) */
export const AppliedMasteryParent = () => (
  <div
    className="relative border py-5 px-6"
    style={{ backgroundColor: GRID_BG, borderColor: `${GOLD}55` }}
    data-testid="applied-mastery-parent"
  >
    <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: GOLD }} />
    <div className="flex items-center gap-2 mb-2">
      <Target className="w-3 h-3" style={{ color: GOLD }} />
      <span className="text-[10px] tracking-[0.4em] font-black uppercase" style={{ color: GOLD }}>
        Execution Over Memorization
      </span>
    </div>
    <h2 className="text-white font-black uppercase leading-tight text-lg md:text-xl">
      Don't just learn the play. <span style={{ color: GOLD }}>Know when to run it.</span>
    </h2>
    <p className="mt-2 text-xs md:text-sm" style={{ color: SUB_TEXT }}>
      Track your scholar's real-time academic and athletic execution below.
    </p>
  </div>
);

export default AppliedMasteryCockpit;
